import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box } from "@mui/material";
import {
  Background,
  Controls,
  MarkerType,
  ReactFlow,
  applyNodeChanges,
  getViewportForBounds,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import CustomNode from "./Components/CustomNode";
import { ColorsConstants, dashboardCardGreenTextColor } from "../../cssUtils/constants";

const nodeTypes = {
  custom: CustomNode,
};

/** Pixels from top of pane to first visible graph row (clears Flow.js title overlay). */
const FIT_VIEW_TOP_INSET = 96;
const FIT_MIN_ZOOM = 0.5;
const FIT_MAX_ZOOM = 2;
const FIT_PADDING = 0.2;

const DEFAULT_EDGE_COLOR = "#b1b1b7";
const OUTGOING_EDGE_COLOR = ColorsConstants.secondaryBackgroundColor;
const INCOMING_EDGE_COLOR = dashboardCardGreenTextColor;
const DIMMED_OPACITY = 0.45;

const Graph = ({ nodes, edges, onNodeClick, onPaneClick, selectedNode, tags, templates }) => {
  const formatNodes = useCallback((nodes) => {
    return nodes.map((node) => ({
      id: node.id.toString(),
      type: "custom",
      position: {
        x: node.pos_x,
        y: node.pos_y,
      },
      data: {
        label: node.name,
        tags,
        templates,
        ...node,
      },
    }));
  }, [tags, templates]);
  
  const [rfNodes, setRfNodes] = useState(() => formatNodes(nodes));
  const [flowInstance, setFlowInstance] = useState(null);
  const containerRef = useRef(null);

  useEffect(() => {
    setRfNodes(formatNodes(nodes));
  }, [nodes, formatNodes]);

  // Every node reachable by following outgoing edges from the selected node, all the way to the end.
  const downstreamNodeIds = useMemo(() => {
    if (!selectedNode) return null;

    const outgoing = new Map();
    edges.forEach((edge) => {
      const source = edge.from_node.toString();
      const target = edge.to_node.toString();
      if (!outgoing.has(source)) outgoing.set(source, []);
      outgoing.get(source).push(target);
    });

    const visited = new Set([selectedNode.id.toString()]);
    const queue = [selectedNode.id.toString()];
    while (queue.length) {
      const current = queue.shift();
      (outgoing.get(current) || []).forEach((next) => {
        if (!visited.has(next)) {
          visited.add(next);
          queue.push(next);
        }
      });
    }

    return visited;
  }, [selectedNode, edges]);

  const displayNodes = useMemo(() => {
    if (!downstreamNodeIds) return rfNodes;

    return rfNodes.map((node) => ({
      ...node,
      style: {
        ...node.style,
        opacity: downstreamNodeIds.has(node.id) ? 1 : DIMMED_OPACITY,
      },
    }));
  }, [rfNodes, downstreamNodeIds]);

  useEffect(() => {
    if (!flowInstance || rfNodes.length === 0) return;
    const el = containerRef.current;
    if (!el) return;

    const applyTopAlignedFit = () => {
      const width = el.clientWidth;
      const height = el.clientHeight;
      if (width < 1 || height < 1) return;

      const liveNodes = flowInstance.getNodes();
      const bounds = flowInstance.getNodesBounds(liveNodes);
      const hasSize = bounds.width > 1 && bounds.height > 1;
      if (!hasSize) {
        flowInstance.fitView({
          padding: FIT_PADDING,
          duration: 300,
        });
        return;
      }

      const vp = getViewportForBounds(
        bounds,
        width,
        height,
        FIT_MIN_ZOOM,
        FIT_MAX_ZOOM,
        FIT_PADDING,
      );
      const y = FIT_VIEW_TOP_INSET - bounds.y * vp.zoom;
      flowInstance.setViewport(
        { x: vp.x, y, zoom: vp.zoom },
        { duration: 300 },
      );
    };

    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(applyTopAlignedFit);
    });
  }, [flowInstance, nodes, edges]);


  const formatEdges = (edges) => {
    return edges.map((edge) => {
      const source = edge.from_node.toString();
      const target = edge.to_node.toString();
      const isDownstream =
        !downstreamNodeIds || downstreamNodeIds.has(source);
      const edgeColor = isDownstream ? OUTGOING_EDGE_COLOR : DEFAULT_EDGE_COLOR;

      return {
        id: edge.id.toString(),
        source,
        target,
        label: edge.label || null,

        type: "smoothstep",
        animated: isDownstream,
        style: {
          stroke: edgeColor,
          strokeWidth: downstreamNodeIds && isDownstream ? 3 : 2,
          opacity: isDownstream ? 1 : DIMMED_OPACITY,
        },

        markerEnd: {
          type: MarkerType.Arrow,
          color: edgeColor,
          width: 15,
          height: 15,
        },
      };
    });
  };

  return (
    <Box ref={containerRef} sx={{ width: "100%", height: "100%" }}>
      <ReactFlow
        onChange={() => console.log(edges)}
        nodes={displayNodes}
        edges={formatEdges(edges)}
        onInit={setFlowInstance}
        minZoom={FIT_MIN_ZOOM}
        maxZoom={FIT_MAX_ZOOM}
        fitViewOptions={{ padding: FIT_PADDING }}
        onNodesChange={(changes) =>
          setRfNodes((nds) => applyNodeChanges(changes, nds))
        }
        onNodeClick={onNodeClick}
        onPaneClick={onPaneClick}
        nodeTypes={nodeTypes}
        snapToGrid
        snapGrid={[15, 15]}
        nodeOrigin={[0.5, 0.5]}
        nodesDraggable={true}
        defaultEdgeOptions={{
          animated: true,
          style: { stroke: "#b1b1b7", strokeWidth: 2 },
        }}
      >
        <Background color="#aaa" gap={16} />
        <Controls />
      </ReactFlow>
    </Box>
  );
};

export default Graph;
